import { useEffect, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

import { cn } from '@/lib/cn';
import { ExperimentCohortSync } from '@/lib/analytics/ExperimentCohortSync';
import { PostPurchaseUpsellMount } from '@/features/upsell/PostPurchaseUpsellMount';
import { StackSetupGate } from './StackSetupGate';

/**
 * OnboardingShell — mock-matched.
 *
 * Single horizontal progress rule above a 'PATIENT INTAKE' label band on
 * the left (with a thicker underline tracking the active step) and a close
 * X on the right. Page body scrolls below; nothing else is rendered in
 * this shell so screens can claim their own sticky bottom CTA.
 *
 * Steps reflect the wizard's actual progression:
 *   welcome → intake → expect → complete
 */
// The post-purchase popup slides in this long after the welcome screen
// appears, rather than arriving with it as the setup screen clears.
const WELCOME_POPUP_DELAY_MS = 1_000;

function AfterDelay({ ms, children }: { ms: number; children: ReactNode }) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setReady(true), ms);
    return () => clearTimeout(timer);
  }, [ms]);
  return ready ? <>{children}</> : null;
}

const STEPS = [
  { path: '/onboarding/welcome', label: 'Welcome' },
  { path: '/onboarding/intake', label: 'Intake' },
  { path: '/onboarding/expect', label: 'What to expect' },
  { path: '/onboarding/complete', label: 'All set' },
];

export function OnboardingShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const activeIndex = Math.max(
    0,
    STEPS.findIndex((s) => location.pathname.startsWith(s.path)),
  );
  const progressPct = ((activeIndex + 1) / STEPS.length) * 100;

  // The welcome step paints its own drawer overlay (with its own header
  // bar + close button), so we hide the shell chrome there to avoid a
  // duplicate "PATIENT INTAKE" eyebrow + close-X stack.
  if (location.pathname.startsWith('/onboarding/welcome')) {
    return (
      <div className="min-h-screen bg-page text-ink">
        {/* Stamp $experiment_* person properties here too — upsell_viewed
            fires from this shell for fresh post-purchase patients, most of
            whom never reach /app (where AppLayout would stamp them). */}
        <ExperimentCohortSync />
        {/* One gate for the screen and its popup, so the popup never opens
            over the account-setup loader. */}
        <StackSetupGate>
          <Outlet />
          {/* Post-purchase upsell — fires only on /onboarding/welcome
              for variant-a cohort with a recent (<24h) purchase whose
              source product has upsells configured. The hook returns
              null for everyone else so this is a no-op for control. */}
          <AfterDelay ms={WELCOME_POPUP_DELAY_MS}>
            <PostPurchaseUpsellMount />
          </AfterDelay>
        </StackSetupGate>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--surface-page)] text-ink flex flex-col">
      <ExperimentCohortSync />
      <header className="border-b border-line-subtle">
        {/* Active-step progress hairline */}
        <div className="h-3 bg-[var(--surface-sunken)] relative">
          <div
            className="absolute inset-y-0 left-0 bg-[var(--surface-inverse)] transition-all duration-base"
            style={{ width: `${progressPct}%` }}
            role="progressbar"
            aria-valuenow={activeIndex + 1}
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
          />
        </div>
        <div className="flex items-stretch">
          {/* Tab-style label that reads as the "current section". */}
          <div className="flex-1 flex items-center px-15 h-45">
            <span className="type-label-md text-ink">PATIENT INTAKE</span>
          </div>
          <button
            type="button"
            aria-label="Exit intake"
            onClick={() => navigate('/app')}
            className={cn(
              'w-45 h-45 flex items-center justify-center text-ink-secondary',
              'border-l border-line-subtle hover:bg-[var(--surface-sunken)]',
            )}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      <main className="flex-1 flex flex-col">
        <Outlet />
      </main>
    </div>
  );
}
